import { useMemo, useState } from "react";
import type { Polygon } from "geojson";

import { CloseEyeIcon, DeleteIcon, MapPinPlusIcon, PenLineIcon, ViewIcon } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { ParkingPaymentModeType, PaymentZoneArea } from "../payment-zone-area.type";
import { PaymentZoneCreationForm } from "./payment-zone-creation-form";
import { PaymentZoneImportDialog } from "./payment-zone-import-dialog";
import { formatPolygonCount, PAYMENT_ZONE_COLORS, zoneCount } from "../parking-payment.const";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogHeader,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";

type DraftView = {
  mode: "create" | "edit";
  status: "form" | "drawing";
  name: string;
  color: [number, number, number, number];
  zoneId?: string;
};

type PaymentZoneAreaCreationAsideProps = {
  open: boolean;
  appMode: ParkingPaymentModeType;
  paymentZones: PaymentZoneArea[];
  onEditZone: (zone: PaymentZoneArea) => void;
  onDeleteZone?: (zone: PaymentZoneArea) => void;
  hiddenZoneIds: Set<string>;
  onToggleZoneVisibility: (zone: PaymentZoneArea) => void;
  draft: DraftView | null;
  onStartCreation: () => void;
  onDraftChange: (updates: Partial<Pick<DraftView, "name" | "color">>) => void;
  onBeginDrawing: () => void;
  onSubmitEdition: () => void;
  onCancelCreation: () => void;
  onImportGeoJson: (polygons: Polygon[]) => boolean;
  isSavingCreation: boolean;
};

export const PaymentZoneAreaCreationAside = ({
  open,
  appMode,
  paymentZones,
  onEditZone,
  onDeleteZone,
  hiddenZoneIds,
  onToggleZoneVisibility,
  draft,
  onStartCreation,
  onDraftChange,
  onBeginDrawing,
  onSubmitEdition,
  onCancelCreation,
  onImportGeoJson,
  isSavingCreation,
}: PaymentZoneAreaCreationAsideProps) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [zoneToDelete, setZoneToDelete] = useState<PaymentZoneArea | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const zoneCountLabel = useMemo(() => {
    return zoneCount(paymentZones);
  }, [paymentZones]);

  const addButtonDisabled = draft !== null || appMode === ParkingPaymentModeType.AddPaymentZoneArea || isSavingCreation;

  const isDrawingDraft = draft?.status === "drawing";
  const isEditingDraft = draft?.mode === "edit";
  const disableZoneActions = isDrawingDraft || isSavingCreation || isEditingDraft;

  const selectedColor = draft?.color ?? PAYMENT_ZONE_COLORS[0].rgba;

  return (
    <>
      <aside
        className={cn(
          "fixed inset-y-4 right-2 z-50 flex w-88 flex-col rounded-xl border border-border bg-background shadow-xl transition-transform duration-200",
          open ? "translate-x-0 pointer-events-auto" : "pointer-events-none translate-x-[calc(100%+1.5rem)]",
        )}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <p className="text-sm font-semibold">Zones</p>
            <p className="text-xs text-muted-foreground">{zoneCountLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button disabled={addButtonDisabled} size="icon" onClick={onStartCreation} title="Ajouter une zone">
              <MapPinPlusIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {draft && (
          <PaymentZoneCreationForm
            mode={draft.mode}
            name={draft?.name ?? ""}
            color={selectedColor}
            isSaving={isSavingCreation}
            isDrawingDraft={isDrawingDraft}
            onNameChange={(value) => onDraftChange({ name: value })}
            onColorSelect={(paletteColor) => onDraftChange({ color: paletteColor })}
            onCancel={onCancelCreation}
            onSubmit={draft.mode === "edit" ? onSubmitEdition : onBeginDrawing}
            onAddPolygon={onBeginDrawing}
            onOpenImport={() => setImportOpen(true)}
          />
        )}
        <ScrollArea className="flex-1 min-h-0 px-5 py-4 [&>[data-slot=scroll-area-scrollbar]]:hidden">
          <div className="space-y-3">
            {paymentZones.length === 0 && !(draft !== null) && (
              <div className="flex h-32 items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 text-center text-xs text-muted-foreground">
                Commencez par ajouter une zone de paiement.
              </div>
            )}

            {[...paymentZones].reverse().map((zone) => {
              const zoneColorArr = zone.color || zone.properties?.color || PAYMENT_ZONE_COLORS[0].rgba;
              const zoneColor = `rgba(${zoneColorArr[0]},${zoneColorArr[1]},${zoneColorArr[2]},${zoneColorArr[3] / 255})`;
              const isHidden = hiddenZoneIds.has(zone.id);

              return (
                <div
                  key={zone.id}
                  className={cn(
                    "rounded-lg border border-border/80 bg-card px-4 py-5 shadow-sm transition-colors",
                    isHidden && "opacity-60",
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <span
                        className="mt-1 h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: zoneColor }}
                        aria-hidden
                      />
                      <div className="mb-4">
                        <p className="text-sm font-semibold">{zone.properties?.name || "Zone sans nom"}</p>
                        <p className="text-xs text-muted-foreground">{formatPolygonCount(zone)}</p>
                      </div>
                    </div>
                    <Badge variant="outline">{isHidden ? "Caché" : "Visible"}</Badge>
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-4">
                    <div>
                      <Button
                        className={`${isHidden ? "bg-secondary/10 hover:bg-secondary" : ""}`}
                        size="icon"
                        onClick={() => onToggleZoneVisibility(zone)}
                        aria-pressed={isHidden}
                        title={isHidden ? "Afficher la zone" : "Masquer la zone"}
                        disabled={disableZoneActions}
                      >
                        {isHidden ? <CloseEyeIcon className="h-4 w-4" /> : <ViewIcon className="h-4 w-4" />}
                        <span className="sr-only">{isHidden ? "Afficher la zone" : "Masquer la zone"}</span>
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="gap-2"
                        onClick={() => onEditZone(zone)}
                        disabled={disableZoneActions}
                      >
                        <PenLineIcon className="h-4 w-4" />
                        Modifier
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          setZoneToDelete(zone);
                          setOpenConfirm(true);
                        }}
                        disabled={!onDeleteZone || disableZoneActions}
                      >
                        <DeleteIcon className="h-4 w-4" />
                        <span className="sr-only">Supprimer la zone</span>
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        <footer className=" rounded-b-xl  border-t border-border bg-background px-5 py-3 text-xs text-muted-foreground font-medium">
          Utilisez le bouton «<MapPinPlusIcon className="h-4 w-4 inline align-text-bottom" /> » pour créer une zone.
          Dessinez les polygones sur la carte puis enregistrez.
        </footer>
      </aside>
      <AlertDialog
        open={openConfirm}
        onOpenChange={(open) => {
          if (!open) {
            setOpenConfirm(false);
            setZoneToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la zone de paiement "{zoneToDelete?.properties?.name}"</AlertDialogTitle>
            <AlertDialogDescription>Êtes-vous sûr de vouloir supprimer cette zone de paiement ?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (zoneToDelete && onDeleteZone) {
                  onDeleteZone(zoneToDelete);
                }
                setOpenConfirm(false);
                setZoneToDelete(null);
              }}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <PaymentZoneImportDialog open={importOpen} onOpenChange={setImportOpen} onImport={onImportGeoJson} />
    </>
  );
};
